import React, { ChangeEvent, forwardRef, useCallback, useEffect, useRef, useState } from 'react';

import { clsx } from 'clsx';

import { BaseTextField, BaseTextFieldProps } from './BaseTextField';

export interface MoneyFieldProps
    extends Omit<BaseTextFieldProps, 'type' | 'step' | 'defaultValue' | 'value'> {
    value?: number;
    defaultValue?: number;
    step?: number;
    errorMessage?: string;
    allowDecimals?: boolean;
}

/**
 * Validates if a string is a valid money input (allows intermediate states like "0." or "0.0")
 */
const isValidMoneyInput = (value: string, allowDecimals: boolean): boolean => {
    if (value === '' || value === '-') return true;

    if (!allowDecimals) {
        return /^-?\d*$/.test(value);
    }

    // Allow intermediate states: ".", "0.", "0.0", "0.00", etc.
    return /^-?\d*\.?\d{0,2}$/.test(value);
};

/**
 * MoneyField - A text input optimized for currency values
 *
 * Uses type="text" with inputMode="decimal" instead of type="number" to:
 * - Allow intermediate states like "0." and "0.0" during typing
 * - Provide consistent behavior across all browsers
 * - Still show numeric keyboard on mobile devices
 */
export const MoneyField = forwardRef<HTMLInputElement, MoneyFieldProps>(
    (
        {
            step = 0.01,
            onChange,
            onBlur,
            onFocus: externalOnFocus,
            onKeyDown: externalOnKeyDown,
            onMouseDown,
            className = '',
            defaultValue,
            value,
            errorMessage = 'This field must be a cash value.',
            allowDecimals = true,
            ...props
        },
        ref,
    ) => {
        const [error, setError] = useState(false);
        // Track the display value separately to allow intermediate states
        const [displayValue, setDisplayValue] = useState<string>(() => {
            if (value !== undefined && value !== null) return String(value);
            if (defaultValue !== undefined && defaultValue !== null) return String(defaultValue);
            return '';
        });
        const isEditingRef = useRef(false);
        const inputRef = useRef<HTMLInputElement>(null);

        // Sync display value with controlled value when not editing
        useEffect(() => {
            if (!isEditingRef.current && value !== undefined) {
                setDisplayValue(value !== null ? String(value) : '');
            }
        }, [value]);

        const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
            // Call external onKeyDown handler first (e.g., for custom Tab navigation)
            externalOnKeyDown?.(e);

            // If external handler prevented default, don't process further
            if (e.defaultPrevented) {
                return;
            }

            // Allow navigation and editing keys
            const allowedKeys = [
                'Backspace',
                'Delete',
                'ArrowLeft',
                'ArrowRight',
                'ArrowUp',
                'ArrowDown',
                'Tab',
                'Enter',
                'Home',
                'End',
            ];

            if (allowedKeys.includes(e.key)) {
                setError(false);
                return;
            }

            // Allow keyboard shortcuts (Ctrl/Cmd + A, C, V, X, Z)
            if (e.ctrlKey || e.metaKey) {
                return;
            }

            const isNumeric = /^[0-9]$/.test(e.key);
            const isDecimal = e.key === '.';
            const isMinus = e.key === '-';

            // Block decimal if not allowed
            if (!allowDecimals && isDecimal) {
                setError(true);
                e.preventDefault();
                return;
            }

            const target = e.target as HTMLInputElement;
            const currentValue = target.value;
            const selectionStart = target.selectionStart ?? currentValue.length;
            const selectionEnd = target.selectionEnd ?? currentValue.length;

            // Build the prospective new value
            const beforeSelection = currentValue.substring(0, selectionStart);
            const afterSelection = currentValue.substring(selectionEnd);
            const prospectiveValue = beforeSelection + e.key + afterSelection;

            // Validate the prospective value
            if (!isValidMoneyInput(prospectiveValue, allowDecimals)) {
                // Only show error for truly invalid input, not for precision limits
                if (!(isNumeric || isDecimal || isMinus)) {
                    setError(true);
                }
                e.preventDefault();
                return;
            }

            setError(false);
        };

        const handleChange = useCallback(
            (event: ChangeEvent<HTMLInputElement>) => {
                isEditingRef.current = true;
                const newValue = event.target.value;

                // Allow empty string and valid money inputs
                if (newValue === '' || isValidMoneyInput(newValue, allowDecimals)) {
                    setDisplayValue(newValue);
                    // Pass through to parent - they'll get the string value
                    onChange?.(event);
                }
            },
            [onChange, allowDecimals],
        );

        const handleBlur = useCallback(
            (event: React.FocusEvent<HTMLInputElement>) => {
                isEditingRef.current = false;

                // Clean up the display value on blur (e.g., "0." -> "0")
                const currentValue = displayValue;
                if (currentValue !== '' && currentValue !== null) {
                    const numericValue = parseFloat(currentValue);
                    if (!Number.isNaN(numericValue)) {
                        // Format to proper decimal places
                        const formattedValue = allowDecimals
                            ? numericValue.toString()
                            : Math.round(numericValue).toString();
                        if (formattedValue !== currentValue) {
                            setDisplayValue(formattedValue);
                        }
                    }
                }

                onBlur?.(event);
            },
            [displayValue, allowDecimals, onBlur],
        );

        const handleFocus = useCallback(
            (event: React.FocusEvent<HTMLInputElement>) => {
                isEditingRef.current = true;
                externalOnFocus?.(event);
            },
            [externalOnFocus],
        );

        // Combine refs
        const combinedRef = useCallback(
            (node: HTMLInputElement | null) => {
                inputRef.current = node;
                if (typeof ref === 'function') {
                    ref(node);
                } else if (ref) {
                    ref.current = node;
                }
            },
            [ref],
        );

        return (
            <BaseTextField
                {...props}
                inputRef={combinedRef}
                type="text"
                inputMode="decimal"
                value={displayValue}
                className={clsx('money-textfield', className)}
                onChange={handleChange}
                onKeyDown={handleKeyDown}
                onBlur={handleBlur}
                onFocus={handleFocus}
                onMouseDown={onMouseDown}
                error={props.error || error}
                helperText={error ? errorMessage : props.helperText}
            />
        );
    },
);

MoneyField.displayName = 'MoneyField';
